import React from 'react';
import { BrandColor } from '@styleseat/brand';
import { Inline } from '@styleseat/ui';
import {
  BodyText,
  IBodyTextProps,
  SeeMore,
  Stack,
} from '../../../ui';
import { clickable } from '../../../../styles';

type RenderTextProps = {
  children: React.ReactNode;
  itemProp?: string;
  key?: string;
};

export type AddressProps = {
  /** City */
  city?: string;
  /** State */
  state?: string;
  /** Postal code */
  zipCode?: string;
  /** Address line 1 */
  address1?: string;
  /** Address line 2 */
  address2?: string;
  /** Location Instructions */
  locationInstructions?: string;
  /** True for mobile business, otherwise false */
  mobileBusiness?: boolean;
  /** Used to render an alternative text node. */
  renderText?: (props: RenderTextProps) => React.ReactElement;
  /** The test ID to use */
  testID?: string;
  /** Whether to display the address as one line or formatted */
  inline?: boolean;
};

const Text: React.FCWithChildren<IBodyTextProps> = props => (
  <BodyText appearance="medium" color={BrandColor.Metal} {...props} />
);

/**
 * Displays address of a pro, including handling for a mobile business
 */
const Address: React.FCWithChildren<AddressProps> = ({
  city,
  state,
  zipCode,
  address1,
  address2,
  locationInstructions,
  mobileBusiness = false,
  renderText,
  testID = 'address-component',
  inline = false,
}) => {
  // List of street address line items
  const streetAddress = [
    ...mobileBusiness
      ? [
        'Mobile Business',
        address2,
      ]
      : [
        address1,
        address2,
      ],
  ].filter(Boolean);
  const cityState = [
    city,
    state,
  ].filter(Boolean).join(', ');
  const cityStateZip = [
    cityState,
    zipCode,
  ].filter(Boolean).join(' ');
  const hasCityStateZip = city || state || zipCode;
  const hasAddress = streetAddress.length || hasCityStateZip;

  const RenderedText = typeof renderText === 'function' ? renderText : Text;
  const Container = inline ? Inline : Stack;

  return hasAddress ? (
    <Stack space={4}>
      <Container
        testID={testID}
        style={clickable}
      >
        {streetAddress.length ? (
          <RenderedText testID="street-address">
            {streetAddress.join(' ')}{hasCityStateZip && inline ? ', ' : ' '}
          </RenderedText>
        ) : null}
        {hasCityStateZip ? (
          <RenderedText testID="city-state-zip">
            {cityStateZip}
          </RenderedText>
        ) : null}
      </Container>
      {locationInstructions ? (
        <SeeMore numberOfLines={2}>
          <RenderedText testID="location-instructions">{locationInstructions}</RenderedText>
        </SeeMore>
      ) : null}
    </Stack>
  ) : null;
};

export default Address;
